(() => {
  const elementName = 'ug-jp-spokesperson-activities';
  if (customElements.get(elementName)) return;

  class SpokespersonActivities extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.stage = this.querySelector('[data-activity-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-activity-slide]')).filter((slide) => slide.closest(elementName) === this);
      this.dots = Array.from(this.querySelectorAll('[data-activity-dot]')).filter((dot) => dot.closest(elementName) === this);
      this.focusState = new Map();
      this.pointer = null;
      this.suppressClickUntil = 0;
      this.loopClones = [];
      this.motionTimer = 0;
      this.motionAnimation = null;
      this.animating = false;
      this.pendingIndex = null;
      const initial = Number.parseInt(this.dataset.initialIndex, 10);
      this.active = this.slides.length ? Math.max(0, Math.min(this.active ?? (Number.isFinite(initial) ? initial : 1), this.slides.length - 1)) : -1;
      const signal = this.controller.signal;
      const listen = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal });

      this.slides.forEach((slide) => {
        slide.querySelectorAll('a, button, input, select, textarea, summary, [tabindex], [contenteditable="true"]').forEach((control) => {
          this.focusState.set(control, control.getAttribute('tabindex'));
        });
      });
      this.querySelectorAll('[data-activity-prev], [data-activity-next]').forEach((button) => {
        if (button.closest(elementName) === this) button.hidden = this.slides.length < 2;
      });
      if (this.stage) this.stage.tabIndex = this.slides.length > 1 ? 0 : -1;

      listen(this, 'click', (event) => this.onClick(event), { capture: true });
      listen(this.stage, 'keydown', (event) => this.onKeydown(event));
      listen(this.stage, 'pointerdown', (event) => {
        if (event.isPrimary === false || event.button !== 0 || this.slides.length < 2) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null };
      }, { passive: true });
      listen(window, 'pointermove', (event) => this.onPointerMove(event), { passive: false });
      listen(window, 'pointerup', (event) => this.onPointerEnd(event), { passive: true });
      listen(window, 'pointercancel', (event) => this.onPointerEnd(event, true), { passive: true });
      listen(window, 'blur', () => { this.pointer = null; });
      listen(this.stage, 'dragstart', (event) => event.preventDefault());
      listen(document, 'shopify:block:select', (event) => this.onEditorSelect(event));
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.pointer = null;
      this.pendingIndex = null;
      this.finishMotion();
      this.focusState?.forEach((tabindex, control) => {
        if (tabindex === null) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', tabindex);
      });
    }

    position(index) {
      const distance = (index - this.active + this.slides.length) % this.slides.length;
      if (distance === 0) return 'active';
      if (distance === 1) return 'next';
      if (distance === this.slides.length - 1) return 'prev';
      return distance <= this.slides.length / 2 ? 'after' : 'before';
    }

    createLoopClone(slide) {
      const style = getComputedStyle(slide);
      const clone = slide.cloneNode(true);
      clone.removeAttribute('data-activity-slide');
      clone.setAttribute('data-activity-ghost', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      [clone, ...clone.querySelectorAll('[id], [data-block-id], [data-shopify-editor-block]')].forEach((node) => {
        node.removeAttribute('id');
        node.removeAttribute('data-block-id');
        node.removeAttribute('data-shopify-editor-block');
      });
      clone.style.transform = style.transform;
      clone.style.opacity = style.opacity;
      clone.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
      this.stage.append(clone);
      this.loopClones.push(clone);
      return clone;
    }

    finishMotion() {
      clearTimeout(this.motionTimer);
      this.motionTimer = 0;
      this.motionAnimation = null;
      this.loopClones?.forEach((clone) => clone.remove());
      this.loopClones = [];
      this.animating = false;
      const pending = this.pendingIndex;
      this.pendingIndex = null;
      if (pending !== null && this.controller) this.select(pending);
    }

    render(animate = false) {
      const duration = animate && this.stage ? Math.max(...getComputedStyle(this.slides[this.active]).transitionDuration.split(',').map((value) => Number.parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000))) : 0;
      const outgoing = [];
      const positions = this.slides.map((slide, index) => this.position(index));
      if (duration > 0) {
        this.slides.forEach((slide, index) => {
          const previous = slide.dataset.position;
          const position = positions[index];
          const side = (value) => value === 'prev' || value === 'before' ? -1 : value === 'next' || value === 'after' ? 1 : 0;
          if (!side(previous) || !side(position) || side(previous) === side(position)) return;
          if (previous === 'prev' || previous === 'next') {
            outgoing.push({ clone: this.createLoopClone(slide), position: previous === 'prev' ? 'before' : 'after' });
          }
          slide.setAttribute('data-activity-preparing', '');
          slide.dataset.position = side(position) < 0 ? 'before' : 'after';
        });
        this.stage.getBoundingClientRect();
      }
      this.slides.forEach((slide, index) => {
        const position = positions[index];
        const active = index === this.active;
        slide.removeAttribute('data-activity-preparing');
        slide.dataset.position = position;
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        this.focusState.forEach((tabindex, control) => {
          if (!slide.contains(control)) return;
          if (!active) control.tabIndex = -1;
          else if (control.tagName === 'A') control.tabIndex = 0;
          else if (tabindex === null) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', tabindex);
        });
      });
      this.dots.forEach((dot) => {
        const index = Number.parseInt(dot.dataset.index, 10);
        dot.hidden = this.slides.length < 2 || index >= this.slides.length;
        if (index === this.active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const status = this.querySelector('[data-activity-status]');
      if (status) status.textContent = `${this.active + 1} / ${this.slides.length}`;
      outgoing.forEach(({ clone, position }) => {
        clone.dataset.position = position;
        clone.style.removeProperty('transform');
        clone.style.removeProperty('opacity');
      });
      if (duration > 0) {
        this.animating = true;
        const motion = this.slides[this.active].getAnimations().find((animation) => animation.transitionProperty === 'transform');
        this.motionAnimation = motion;
        motion?.finished.then(() => {
          if (this.motionAnimation === motion) this.finishMotion();
        }, () => {});
        this.motionTimer = setTimeout(() => this.finishMotion(), duration + 80);
      }
    }

    step(direction) {
      this.select((this.pendingIndex ?? this.active) + direction);
    }

    select(index) {
      if (!this.slides.length || !Number.isFinite(index)) return;
      const next = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      if (this.animating) {
        this.pendingIndex = next;
        return;
      }
      if (next === this.active) return;
      if (this.slides[this.active]?.contains(document.activeElement)) this.stage?.focus({ preventScroll: true });
      this.active = next;
      this.render(true);
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      if (!target || target.closest(elementName) !== this) return;
      const slide = target.closest('[data-activity-slide]');
      if (slide && event.detail !== 0 && performance.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressClickUntil = 0;
        return;
      }
      if (target.closest('[data-activity-prev]')) this.step(-1);
      else if (target.closest('[data-activity-next]')) this.step(1);
      else if (target.closest('[data-activity-dot]')) this.select(Number.parseInt(target.closest('[data-activity-dot]').dataset.index, 10));
      else if (slide && this.slides.indexOf(slide) !== this.active) {
        event.preventDefault();
        event.stopPropagation();
        this.select(this.slides.indexOf(slide));
      }
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      let index;
      if (event.key === 'ArrowLeft') index = (this.pendingIndex ?? this.active) - 1;
      if (event.key === 'ArrowRight') index = (this.pendingIndex ?? this.active) + 1;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = this.slides.length - 1;
      if (index === undefined || this.slides.length < 2) return;
      event.preventDefault();
      this.select(index);
    }

    onPointerMove(event) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      const x = Math.abs(event.clientX - pointer.x);
      const y = Math.abs(event.clientY - pointer.y);
      if (!pointer.axis && Math.max(x, y) >= 8) pointer.axis = x > y * 1.15 ? 'x' : 'y';
      if (pointer.axis === 'x') {
        this.suppressClickUntil = performance.now() + 500;
        if (event.cancelable) event.preventDefault();
      }
    }

    onPointerEnd(event, cancelled = false) {
      const pointer = this.pointer;
      if (!pointer || pointer.id !== event.pointerId) return;
      this.pointer = null;
      if (pointer.axis !== 'x') return;
      this.suppressClickUntil = performance.now() + 500;
      const x = event.clientX - pointer.x;
      const y = event.clientY - pointer.y;
      if (!cancelled && Math.abs(x) >= 36 && Math.abs(x) > Math.abs(y) * 1.15) this.step(x < 0 ? 1 : -1);
    }

    onEditorSelect(event) {
      const owner = event.target instanceof Element ? event.target.closest(elementName) : null;
      if (owner && owner !== this) return;
      const wrapper = this.closest('[id^="shopify-section-"]');
      if (event.detail?.sectionId && wrapper && wrapper.id !== `shopify-section-${event.detail.sectionId}`) return;
      const index = this.slides.findIndex((slide) => slide.dataset.blockId === String(event.detail?.blockId));
      if (index !== -1) this.select(index);
    }
  }

  customElements.define(elementName, SpokespersonActivities);
})();
